import React, { useEffect, useRef, useState } from 'react'
import { LinkIcon, ArrowPathIcon } from '@heroicons/react/24/outline'
import { fetchRecycleEvents, shortTx, type RecycleEvent } from '../services/recycleEventsApi'

const POLL_MS = 10000
const DEFAULT_MAX_ROWS = 20

/** Prefer scan-local timestamps (no Z) without shifting UTC → local by 8h. */
const formatTime = (value: string | null | undefined) => {
  if (!value) return '—'
  const trimmed = String(value).trim()
  // "YYYY-MM-DD HH:mm:ss" or "YYYY-MM-DDTHH:mm:ss" without timezone → show as-is (station local)
  const localMatch = trimmed.match(
    /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?/
  )
  if (localMatch && !/[zZ]|[+-]\d{2}:?\d{2}$/.test(trimmed)) {
    const [, y, mo, d, h, mi, s] = localMatch
    const date = new Date(
      Number(y),
      Number(mo) - 1,
      Number(d),
      Number(h),
      Number(mi),
      Number(s || 0)
    )
    return date.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    })
  }
  const ms = Date.parse(trimmed)
  if (!Number.isFinite(ms)) return trimmed
  return new Date(ms).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  })
}

const displayEventTime = (row: RecycleEvent) =>
  formatTime(row.collectionDate || row.createdAt)

export interface LiveRecycleFeedProps {
  variant?: 'embedded' | 'page'
  limit?: number
  title?: string
  subtitle?: string
  showHeading?: boolean
}

const LiveRecycleFeed: React.FC<LiveRecycleFeedProps> = ({
  variant = 'embedded',
  limit = DEFAULT_MAX_ROWS,
  title = 'On-chain recycle events',
  subtitle = 'Scans from the recycling station appear here after a successful OneChain commit.',
  showHeading = true
}) => {
  const maxRows = Math.min(Math.max(limit, 1), 100)
  const [events, setEvents] = useState<RecycleEvent[]>([])
  const [latestId, setLatestId] = useState(0)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null)
  const latestIdRef = useRef(0)
  const bootstrappedRef = useRef(false)

  useEffect(() => {
    let cancelled = false
    latestIdRef.current = 0
    bootstrappedRef.current = false
    setLoading(true)

    const pull = async () => {
      try {
        if (!bootstrappedRef.current) {
          const payload = await fetchRecycleEvents({ limit: maxRows })
          if (cancelled) return
          setEvents(payload.events)
          latestIdRef.current = payload.latestId || 0
          setLatestId(latestIdRef.current)
          bootstrappedRef.current = true
        } else if (latestIdRef.current > 0) {
          const payload = await fetchRecycleEvents({
            limit: maxRows,
            sinceId: latestIdRef.current
          })
          if (cancelled) return
          if (payload.events.length > 0) {
            setEvents((prev) => {
              const merged = [...payload.events].reverse().concat(prev)
              const seen = new Set<number>()
              const unique: RecycleEvent[] = []
              for (const row of merged) {
                if (seen.has(row.id)) continue
                seen.add(row.id)
                unique.push(row)
                if (unique.length >= maxRows) break
              }
              return unique
            })
            latestIdRef.current = Math.max(
              latestIdRef.current,
              payload.latestId || 0,
              ...payload.events.map((e) => e.id)
            )
            setLatestId(latestIdRef.current)
          }
        } else {
          const payload = await fetchRecycleEvents({ limit: maxRows })
          if (cancelled) return
          setEvents(payload.events)
          latestIdRef.current = payload.latestId || 0
          setLatestId(latestIdRef.current)
        }

        setError(null)
        setLastUpdated(new Date())
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load live feed')
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    pull()
    const timer = window.setInterval(pull, POLL_MS)
    return () => {
      cancelled = true
      window.clearInterval(timer)
    }
  }, [maxRows])

  const sectionClass =
    variant === 'page'
      ? 'relative py-4 md:py-6 px-4 md:px-6 max-w-[1400px] mx-auto'
      : 'relative py-16 md:py-20 px-6 md:px-12 max-w-[1400px] mx-auto'

  return (
    <section className={sectionClass}>
      {showHeading && (
        <div className={`flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 ${variant === 'page' ? 'mb-4' : 'mb-8'}`}>
          <div>
            <p className="supplementary-type text-xs mb-1">Live station feed</p>
            <h2 className="font-display text-3xl md:text-4xl font-bold text-primary-900">
              {title}
            </h2>
            <p className={`text-sm text-gray-600 max-w-xl ${variant === 'page' ? 'mt-1' : 'mt-2'}`}>{subtitle}</p>
          </div>
          <div className="flex items-center gap-2 text-xs text-gray-500">
            <ArrowPathIcon className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            <span>
              {lastUpdated
                ? `Updated ${lastUpdated.toLocaleTimeString()}`
                : 'Connecting…'}
              {latestId > 0 ? ` · #${latestId}` : ''}
            </span>
          </div>
        </div>
      )}

      {!showHeading && (
        <div className="flex justify-end mb-4">
          <div className="flex items-center gap-2 text-xs text-gray-500">
            <ArrowPathIcon className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            <span>
              {lastUpdated
                ? `Updated ${lastUpdated.toLocaleTimeString()}`
                : 'Connecting…'}
              {latestId > 0 ? ` · #${latestId}` : ''}
            </span>
          </div>
        </div>
      )}

      {error && (
        <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          {error}
        </div>
      )}

      <div className="overflow-hidden rounded-xl border border-primary-100 bg-white/80 shadow-sm backdrop-blur">
        <div className="overflow-x-auto">
          <table className="min-w-full text-left text-sm">
            <thead className="bg-primary-50/80 text-xs uppercase tracking-wide text-primary-800">
              <tr>
                <th className="px-4 py-3 font-semibold">Time</th>
                <th className="px-4 py-3 font-semibold">Product</th>
                <th className="px-4 py-3 font-semibold">Barcode</th>
                <th className="px-4 py-3 font-semibold">Location</th>
                <th className="px-4 py-3 font-semibold">Status</th>
                <th className="px-4 py-3 font-semibold">Tx</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {events.length === 0 && !loading && (
                <tr>
                  <td colSpan={6} className="px-4 py-10 text-center text-gray-500">
                    No recycle events yet. Scan a bottle on the station to see it here.
                  </td>
                </tr>
              )}
              {events.map((row) => (
                <tr key={row.id} className="hover:bg-primary-50/40 transition-colors">
                  <td className="px-4 py-3 whitespace-nowrap text-gray-600 tabular-nums">
                    {displayEventTime(row)}
                  </td>
                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-900">
                      {row.name || row.nameEn || row.nameZh || 'Unknown'}
                    </div>
                    <div className="text-xs text-gray-500">
                      {[row.brand, row.packaging, row.weightG != null ? `${row.weightG}g` : null]
                        .filter(Boolean)
                        .join(' · ') || '—'}
                    </div>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-gray-700">
                    {row.barcode}
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-600 max-w-[10rem] truncate" title={row.location || undefined}>
                    {row.location || '—'}
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium border ${
                        row.status === 'ONCHAIN'
                          ? 'bg-emerald-50 text-emerald-800 border-emerald-200'
                          : 'bg-amber-50 text-amber-800 border-amber-200'
                      }`}
                    >
                      {row.status || '—'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {row.txHash && row.txUrl ? (
                      <a
                        href={row.txUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 font-mono text-xs text-primary-600 hover:text-primary-800"
                        title={row.txHash}
                      >
                        {shortTx(row.txHash)}
                        <LinkIcon className="h-3.5 w-3.5" />
                      </a>
                    ) : (
                      <span className="text-xs text-gray-400">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  )
}

export default LiveRecycleFeed
